import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@tanium/react-confirm-modal';
import { FormRow } from '@tanium/react-form';
import { TextInput } from '@tanium/react-text-input';
import { MediumBoldText } from '../../TextStyles';
import { AsyncState, AsyncStatus } from '../../hooks/useAsync';
import { CaseGrant, CaseRole } from '../../models/CaseManagementModels';
import { TaniumSelect } from '../../models/TaniumSelectModels';
import { isValidEmail } from '../../utilities/emailHelper';
import { CaseRoleOption, useCaseRoleOptions } from './CaseManagementSection';

interface CaseGrantModalProps {
  crmAccountId: string;
  grant?: CaseGrant;
  isOpen: boolean;
  onRequestClose: () => void;
  upsertCaseGrant: AsyncState<void, (crmAccountId: string, email: string, role: CaseRole) => Promise<void>>;
}

// A single modal covers both add (grant undefined, email editable) and edit (grant defined,
// email fixed) since both funnel into the same upsertCaseGrant call -- editing a grant's role
// is just an upsert keyed by its existing email.
export const CaseGrantModal: React.VFC<CaseGrantModalProps> = ({
  crmAccountId,
  grant,
  isOpen,
  onRequestClose,
  upsertCaseGrant,
}) => {
  const { t } = useTranslation();
  const roleOptions = useCaseRoleOptions();
  const isEditMode = Boolean(grant);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<CaseRole>(CaseRole.CaseUser);
  const trimmedEmail = email.trim();
  const isEmailInvalid = trimmedEmail.length > 0 && !isValidEmail(trimmedEmail);

  // This modal instance is reused across opens rather than remounted, so its fields need to be
  // (re)seeded from `grant` every time it opens rather than just once on mount.
  useEffect(() => {
    if (isOpen) {
      setEmail(grant?.email ?? '');
      setRole(grant?.role ?? CaseRole.CaseUser);
    }
  }, [isOpen, grant]);

  useEffect(() => {
    if (isOpen && (upsertCaseGrant.status === AsyncStatus.Success || upsertCaseGrant.status === AsyncStatus.Failure)) {
      upsertCaseGrant.reset();
      onRequestClose();
    }
  }, [isOpen, upsertCaseGrant, onRequestClose]);

  return (
    <ConfirmModal
      isOpen={isOpen}
      header={isEditMode ? t('Edit Support Case Access') : t('Add Support Case Access')}
      confirmLabel={isEditMode ? t('Save') : t('Add')}
      onConfirm={async () => {
        await upsertCaseGrant.execute(crmAccountId, trimmedEmail, role);
      }}
      isConfirmDisabled={upsertCaseGrant.status === AsyncStatus.Pending || !trimmedEmail || isEmailInvalid}
      onCancel={onRequestClose}
    >
      <FormRow label={t('Email address')} required errors={isEmailInvalid ? t('Enter a valid email address.') : ''}>
        {isEditMode ? (
          <MediumBoldText>{grant?.email}</MediumBoldText>
        ) : (
          <TextInput
            aria-label={t('Email address')}
            placeholder={t('Email address')}
            type="email"
            value={email}
            invalid={isEmailInvalid}
            onChange={(e) => setEmail(e.target.value.toLowerCase())}
          />
        )}
      </FormRow>
      <FormRow label={t('Role')} required>
        <TaniumSelect
          aria-label={t('Role')}
          isMulti={false}
          options={roleOptions}
          value={roleOptions.find((option) => option.value === role)}
          onChange={(option: CaseRoleOption) => setRole(option.value)}
        />
      </FormRow>
    </ConfirmModal>
  );
};
